import {
  TTaxAndAccountingOnboarding,
  TTaxAndAccountingOnboardingAnswers,
} from '@commenda/api';
import { Country } from '@commenda/prisma';
import { PROFILE_FIELD_CATALOG } from './fieldCatalog';
import { ProfileFieldKey } from './fieldCatalog.types';
import { getProfileFields, getRequiredFields } from './countryConfig';
import { IntakeEntityFacts, WizardFormData } from './wizardLogic.types';

/** Part 1: a fiscal year must be chosen and the entity must carry the required facts. */
export function validateIntake(
  entity: IntakeEntityFacts,
  selectedFiscalYear: number | null
): { ok: boolean; message?: string } {
  const hasEntityDetails =
    Boolean(entity.incorporationCountry) &&
    Boolean(entity.formationDate) &&
    entity.fiscalYearEndMonth != null &&
    entity.fiscalYearEndDay != null;

  if (!hasEntityDetails) {
    return {
      ok: false,
      message:
        'Please complete the entity details (country, formation date and fiscal-year end) before continuing.',
    };
  }

  if (!selectedFiscalYear) {
    return { ok: false, message: 'Please select a fiscal year.' };
  }

  return { ok: true };
}

/**
 * Part 2: COMPLETE requires every fact in the entity's per-country required set (from `countryConfig`).
 * Each fact's answered-ness is decided by its catalog entry. Returns per-field errors keyed by field id.
 */
export function validateProfile(
  formData: WizardFormData,
  country: Country | undefined
): {
  ok: boolean;
  errors: Record<string, string>;
} {
  const errors: Record<string, string> = {};
  const required = 'This answer is required.';

  getRequiredFields(country).forEach((key) => {
    if (!PROFILE_FIELD_CATALOG[key].isAnswered(formData[key])) {
      errors[key] = required;
    }
  });

  return { ok: Object.keys(errors).length === 0, errors };
}

/**
 * Maps the wizard form data into the backend profile answer shape, using only the fields the entity's
 * country configures. Fields not in the country's config (e.g. a stale value in a resumed draft) are
 * never emitted, so unconfigured facts are never attested.
 */
export function buildProfileAnswers(
  formData: WizardFormData,
  country: Country | undefined
): TTaxAndAccountingOnboardingAnswers {
  const answers: TTaxAndAccountingOnboardingAnswers = {};

  getProfileFields(country).forEach((key) => {
    const value = PROFILE_FIELD_CATALOG[key].toAnswerValue(formData[key]);
    if (value !== undefined) (answers as Record<string, unknown>)[key] = value;
  });

  return answers;
}

/**
 * Seeds the wizard's Part 2 form data from a fetched profile, for the entity's configured fields only:
 * the fiscal year's own answers take precedence, carry-forward pre-fill fills the gaps (never a future
 * year). Booleans are mapped to the 'yes' / 'no' the radio fields use. Returns the seed plus a per-key
 * map of the source fiscal year for any pre-filled value.
 */
export function seedProfileFormData(
  profile: TTaxAndAccountingOnboarding | undefined,
  country: Country | undefined
): {
  seed: WizardFormData;
  prefilledFrom: Partial<Record<ProfileFieldKey, number>>;
} {
  const seed: WizardFormData = {};
  const prefilledFrom: Partial<Record<ProfileFieldKey, number>> = {};
  if (!profile) return { seed, prefilledFrom };

  const own = (profile.answers ?? {}) as TTaxAndAccountingOnboardingAnswers;
  const prefill = profile.prefilledValues ?? {};

  getProfileFields(country).forEach((key) => {
    const entry = PROFILE_FIELD_CATALOG[key];

    const ownValue = own[key];
    if (ownValue !== undefined && ownValue !== null) {
      seed[key] = entry.toFormValue(ownValue);
      return;
    }

    // Pre-fill values are tagged by kind (`{ type, value }`); unwrap to the raw value.
    const field = prefill[key];
    const prefillValue = field?.value.value;
    if (field && prefillValue !== undefined && prefillValue !== null) {
      seed[key] = entry.toFormValue(prefillValue);
      prefilledFrom[key] = field.sourceFiscalYear;
    }
  });

  return { seed, prefilledFrom };
}
